/**
 * @file ClientsTab.js
 * @description Clients tab component for the Multi-tenant SSO Wizard.
 */

import React, { useState, useEffect } from "react";
import { fetchSystemSSOClients } from "../../../services/ssoService";
import Spinner from "../../ui/spinners/Spinner";
import Toast from "../../ui/toasts/Toast";
import styles from "../sso.module.scss";
import NotFoundBox from "../../ui/no_data_indicators/NotFoundBox";
import { getText } from '../../../i18n';

const ClientsTab = ({ ssoId, onClientsLoaded }) => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "clientName",
    direction: "asc",
  });

  useEffect(() => {
    loadClients();
  }, [ssoId]);

  const loadClients = async () => {
    try {
      setIsLoading(true);
      if (ssoId) {
        const fetchedClients = await fetchSystemSSOClients(ssoId);
        setClients(fetchedClients);
        onClientsLoaded(fetchedClients?.length > 0);
      }
    } catch (error) {
      console.error("Failed to fetch SSO clients", error);
      Toast.error(getText('msg_a22680749e40'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedClients = React.useMemo(() => {
    const sortableClients = [...clients];
    if (sortConfig.key) {
      sortableClients.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableClients;
  }, [clients, sortConfig]);

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return (
      <span className={`px-2 ${styles.sortArrowIcon}`}>
        <i
          className={`bi bi-chevron-${
            sortConfig.direction === "asc" ? "up" : "down"
          }`}
        ></i>
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Live: "bg-success",
      Disabled: "bg-secondary",
      "In Progress": "bg-primary",
      Testing: "bg-warning text-dark",
    };
    return (
      <span className={`badge ${statusClasses[status] || "bg-info"}`}>
        {status}
      </span>
    );
  };

  if (isLoading) {
    return <Spinner width={50} className={styles.loadingSpinner} />;
  }

  return (
    <div className="col-12 col-sm-12 col-md-12 col-lg-8 col-xl-6 m-auto">
      {clients.length === 0 ? (
        <NotFoundBox
          backgroundColor="#f0f4ff"
          title={getText('msg_7cc6a6e8c21a')}
          description={getText('msg_13764251e99b')}
        />
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle ing-table-striped">
            <thead className="custom-table-header table-light">
              <tr>
                <th
                  onClick={() => handleSort("clientName")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_709a23220f2c')} {renderSortArrow("clientName")}
                </th>
                <th
                  onClick={() => handleSort("clientCode")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_6346915c34e2')} {renderSortArrow("clientCode")}
                </th>
                <th
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer" }}
                >
                  {getText('msg_e6cff3028928')} {renderSortArrow("status")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedClients.map((client) => (
                <tr key={client.tenantSSOId}>
                  <td>{client.clientName}</td>
                  <td>{client.clientCode}</td>
                  <td>{getStatusBadge(client.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientsTab;
